import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { RiDragMove2Line } from "react-icons/ri";
import { getAuthHeaders } from '../utils/getAuthHeaders'
import { getErrorMessage, BACKEND_FAIL_MESSAGE } from '../utils/getErrorMessage'
import { endpoints, urlAgentLibraryAdminDetails } from "../config/api";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { buildOrgMultiSelectOptions, parseOrgIdsFromMultiSelectValues } from "../utils/orgMultiSelectOptions";
import { refreshAgentCache } from "../utils/refreshAgentCache";
import IndustryContextPicker from "./IndustryContextPicker";
import { resolveIndustrySelection } from "../lib/industryFilter";
import { fetchSectorIndustryList } from "../utils/sectorIndustryList";
import {
  applyIndustryDefaultsToMetadata,
  isPartialIndustrySelection,
} from "../utils/industryMetadata";

// Schedule UI constants (aligned with ScheduleLibraryAgents payload)
const SCHEDULE_DAY_MAP = [
  { label: "S", name: "Sunday" },
  { label: "M", name: "Monday" },
  { label: "T", name: "Tuesday" },
  { label: "W", name: "Wednesday" },
  { label: "T", name: "Thursday" },
  { label: "F", name: "Friday" },
  { label: "S", name: "Saturday" },
];
const SCHEDULE_MAX_REPEAT = { Minute: 10080, Hour: 168, Day: 730, Week: 104, Month: 24 };
const SCHEDULE_TIMEZONES = [
  { value: "Asia/Kolkata", label: "India (IST) GMT+5:30" },
  { value: "GMT", label: "GMT GMT+0:00" },
  { value: "America/Los_Angeles", label: "USA (PST) GMT-8:00" },
  { value: "America/New_York", label: "USA (EST) GMT-5:00" },
  { value: "America/Chicago", label: "USA (CST) GMT-6:00" },
  { value: "Asia/Singapore", label: "Singapore (SGT) GMT+8:00" },
  { value: "Asia/Taipei", label: "Taiwan (CST) GMT+8:00" },
  { value: "Asia/Dubai", label: "UAE (GST) GMT+4:00" },
];

const stepId = () => `step-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const parseInstructionSteps = (instructionsStr) => {
  if (!instructionsStr || typeof instructionsStr !== "string") return [{ id: stepId(), content: "" }];
  const parts = instructionsStr.split(/\s*\|\|\s*/).map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return [{ id: stepId(), content: "" }];
  return parts.map((content) => ({ id: stepId(), content }));
};

const AutoResizeTextarea = ({ value, onChange, style, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 56)}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      style={{ ...style, resize: "none", overflow: "hidden" }}
      {...props}
    />
  );
};

const EditAgent = ({ agent, onClose }) => {
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [instructionSteps, setInstructionSteps] = useState([{ id: stepId(), content: "" }]);
  const [draggedStepIndex, setDraggedStepIndex] = useState(null);
  const [dragOverStepIndex, setDragOverStepIndex] = useState(null);
  const [customAgentName, setCustomAgentName] = useState("");
  const [customAgentFlowId, setCustomAgentFlowId] = useState("");
  const [agentLibraryId, setAgentLibraryId] = useState("");
  const [webSearch, setWebSearch] = useState("Off");
  const [metadata, setMetadata] = useState({});
  const [lookbackPeriod, setLookbackPeriod] = useState("");
  const [lookbackUnit, setLookbackUnit] = useState("Month");
  const [webLinks, setWebLinks] = useState([]);
  const [currentLink, setCurrentLink] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [currentSchedule, setCurrentSchedule] = useState({
    date: "",
    time: "",
    timezone: "Asia/Kolkata",
    type: "once",
    repeatEvery: 1,
    repeatUnit: "Hour",
    repeatDays: { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false },
    endOption: "after",
    endsOn: "",
    endsAfter: 10,
    runDuringOption: "Financial Hour",
  });
  const [autoDataSelection, setAutoDataSelection] = useState(false);
  const [availableForms, setAvailableForms] = useState({ India: {}, USA: {} });
  const [selectedForms, setSelectedForms] = useState({ India: [], USA: [] });
  const [formSearch, setFormSearch] = useState({ India: "", USA: "" });
  const [writingStyle, setWritingStyle] = useState("");
  const [customWritingStyleId, setCustomWritingStyleId] = useState("");
  const [customWritingStyleName, setCustomWritingStyleName] = useState("");
  const [customWritingStyleDescription, setCustomWritingStyleDescription] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryIsCustom, setCategoryIsCustom] = useState(false);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [industryContext, setIndustryContext] = useState({
    country: "India",
    sector: "",
    industry: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          endpoints.agentsCategories,
          { headers: { ...getAuthHeaders() } }
        );
        setCategories(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await axios.get(
          endpoints.orgs,
          { headers: { ...getAuthHeaders() } }
        );
        setAvailableOrgs(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching orgs", err);
      }
    };
    fetchOrgs();
  }, []);

  useEffect(() => {
    if (categories.length > 0 && category && !categories.includes(category)) {
      setCategoryIsCustom(true);
    }
  }, [categories, category]);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const response = await axios.get(
          endpoints.agentsForms,
          {
            headers: {
              ...getAuthHeaders(),
            },
          }
        );
        setAvailableForms(response.data);
      } catch (error) {
        console.error("Error fetching form types", error);
      }
    };
    fetchForms();
  }, []);

  const hydrateIndustryFromMetadata = async (meta) => {
    try {
      const { sectors, flattened } = await fetchSectorIndustryList();
      setIndustryContext(resolveIndustrySelection(meta, flattened, sectors));
    } catch (err) {
      console.error("Error resolving industry from metadata", err);
      const industry = meta?.CATEGORIES?.[0] || "";
      setIndustryContext({
        country: meta?.stock_country || "India",
        sector: "",
        industry,
      });
    }
  };

  const handleFetch = async () => {
    if (!agentLibraryId) {
      alert("Please enter an Agent Library ID");
      return;
    }
    try {
      const response = await axios.get(
        urlAgentLibraryAdminDetails(agentLibraryId),
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );
      const { name, category, Instructions, instructions: instructionsResp, Description, metadata, organization_ids } =
        response.data;
      setCustomAgentName(name || "");
      setCategory(category || "");
      setInstructionSteps(parseInstructionSteps(instructionsResp ?? Instructions ?? ""));
      setDescription(Description || "");
      setMetadata(metadata || {});
      setLookbackPeriod(metadata?.time_period_value || "");
      setLookbackUnit(metadata?.time_period_unit || "Month");
      setWebSearch(metadata?.internet || "Off");
      setWebLinks(metadata?.webLinks || []);
      setSchedules(metadata?.schedules || []);
      setAutoDataSelection(metadata?.autoAddUpcoming || false);
      const formTypes = metadata?.form_types || { India: [], USA: [] };
      setSelectedForms({
        India: formTypes.India || [],
        USA: formTypes.USA || [],
      });
      setWritingStyle(metadata?.writing_style || "");
      setCustomWritingStyleId(metadata?.custom_writing_style_id || "");
      setCustomWritingStyleName(metadata?.custom_writing_style_name || "");
      setCustomWritingStyleDescription(metadata?.custom_writing_style_description || "");
      setSelectedOrgIds(Array.isArray(organization_ids) ? organization_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id)) : []);
      await hydrateIndustryFromMetadata(metadata || {});
    } catch (error) {
      console.error("Error fetching agent details", error);
      alert("Failed to fetch agent details");
    }
  };
  useEffect(() => {
    // Scroll to top when component mounts or agent changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (agent) {
      setAgentLibraryId(agent.id || "");
      if (agent.id) {
        // Auto-fetch when agent ID is provided
        const fetchAgentDetails = async () => {
          try {
            const response = await axios.get(
              urlAgentLibraryAdminDetails(agent.id),
              {
                headers: {
                  ...getAuthHeaders(),
                },
              }
            );
            const { name, category, Instructions, instructions: instructionsResp, Description, metadata, organization_ids } =
              response.data;
            setCustomAgentName(name || "");
            setCategory(category || "");
            setInstructionSteps(parseInstructionSteps(instructionsResp ?? Instructions ?? ""));
            setDescription(Description || "");
            setMetadata(metadata || {});
            setLookbackPeriod(metadata?.time_period_value || "");
            setLookbackUnit(metadata?.time_period_unit || "Month");
            setWebSearch(metadata?.internet || "Off");
            setWebLinks(metadata?.webLinks || []);
            setSchedules(metadata?.schedules || []);
            setAutoDataSelection(metadata?.autoAddUpcoming || false);
            const formTypes = metadata?.form_types || { India: [], USA: [] };
            setSelectedForms({
              India: formTypes.India || [],
              USA: formTypes.USA || [],
            });
            setWritingStyle(metadata?.writing_style || "");
            setCustomWritingStyleId(metadata?.custom_writing_style_id || "");
            setCustomWritingStyleName(metadata?.custom_writing_style_name || "");
            setCustomWritingStyleDescription(metadata?.custom_writing_style_description || "");
            setSelectedOrgIds(Array.isArray(organization_ids) ? organization_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id)) : []);
            await hydrateIndustryFromMetadata(metadata || {});
          } catch (error) {
            console.error("Error fetching agent details", error);
            alert("Failed to fetch agent details");
          }
        };
        fetchAgentDetails();
      }
    }
  }, [agent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agentLibraryId) {
      alert("Agent Library ID is required");
      return;
    }
    if (isPartialIndustrySelection(industryContext)) {
      alert("Complete country, sector, and industry for category defaults, or clear all three.");
      return;
    }
    const instructions = instructionSteps
      .filter((s) => (s.content || "").trim())
      .map((s) => s.content.trim())
      .join(" || ");

    const payload = {
      agent_library_id: agentLibraryId,
    };
    if (description) payload.description = description;
    if (category) payload.agent_category = category;
    if (instructions) payload.instructions = instructions;
    if (customAgentName) payload.custom_agent_name = customAgentName;
    if (customAgentFlowId) payload.custom_agent_flow_id = customAgentFlowId;
    payload.organization_ids = Array.isArray(selectedOrgIds)
      ? selectedOrgIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
      : [];
    const mergedMetadata = {
      ...(metadata || {}),
      time_period_value: lookbackPeriod,
      time_period_unit: lookbackUnit,
      internet: webSearch,
      webLinks: webLinks,
      autoAddUpcoming: autoDataSelection,
      form_types: selectedForms,
      schedules: schedules,
      writing_style: writingStyle || undefined,
      custom_writing_style_id: writingStyle === "custom" ? (customWritingStyleId || undefined) : undefined,
      custom_writing_style_name: writingStyle === "custom" ? (customWritingStyleName || undefined) : undefined,
      custom_writing_style_description: writingStyle === "custom" ? (customWritingStyleDescription || undefined) : undefined,
    };
    payload.metadata = applyIndustryDefaultsToMetadata(mergedMetadata, industryContext);

    try {
      await axios.post(
        endpoints.agentsEditAgentDetails,
        payload,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );
      await refreshAgentCache();
      alert("Agent updated successfully");
    } catch (error) {
      console.error("Error updating agent", error);
      alert("Failed to update agent");
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setDeleteConfirmText("");
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText("");
  };

  const handleDeleteConfirm = async () => {
    if ((deleteConfirmText || "").trim().toLowerCase() !== "delete") return;
    const libraryId = agentLibraryId || agent?.id;
    if (!libraryId) {
      alert("No agent library ID to delete");
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(endpoints.agentsLibraryDelete, {
        data: { agentId: libraryId },
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });
      await refreshAgentCache();
      setShowDeleteModal(false);
      setDeleteConfirmText("");
      alert("Library agent deleted successfully");
      if (onClose) onClose();
    } catch (error) {
      console.error("Error deleting library agent", error);
      alert(getErrorMessage(error, "Failed to delete library agent") + ". " + BACKEND_FAIL_MESSAGE);
    } finally {
      setDeleting(false);
    }
  };

  const reorderInstructionSteps = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setInstructionSteps((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  };

  const handleFormSelection = (country, formKey, isChecked) => {
    setSelectedForms((prev) => {
      const currentList = prev[country] || [];
      if (isChecked) {
        return { ...prev, [country]: [...currentList, formKey] };
      } else {
        return { ...prev, [country]: currentList.filter((key) => key !== formKey) };
      }
    });
  };

  // Build schedule object for metadata (same shape as ScheduleLibraryAgents payload)
  const buildScheduleFromCurrent = () => {
    const type = currentSchedule.type === "weekday" ? "weekdays" : currentSchedule.type;
    const schedule = {
      date: currentSchedule.date,
      time: currentSchedule.time,
      timezone: currentSchedule.timezone,
      type,
    };
    if (currentSchedule.type === "custom") {
      const rev = currentSchedule.repeatEvery;
      schedule.repeatEvery = typeof rev === "number" ? rev : (rev === "" ? 1 : parseInt(rev, 10) || 1);
      schedule.repeatUnit = (currentSchedule.repeatUnit || "day").toLowerCase();
      const repeatOn = Object.entries(currentSchedule.repeatDays || {})
        .filter(([, active]) => active)
        .map(([day]) => day);
      if (repeatOn.length > 0) schedule.repeatOn = repeatOn;
      if (currentSchedule.endOption === "on" && currentSchedule.endsOn) schedule.endsOn = currentSchedule.endsOn;
      if (currentSchedule.endOption === "after" && currentSchedule.endsAfter != null) schedule.endsAfter = currentSchedule.endsAfter;
    }
    const showRunDuring = currentSchedule.type === "hourly" ||
      (currentSchedule.type === "custom" && ["Minute", "Hour"].includes(currentSchedule.repeatUnit));
    if (showRunDuring) {
      const opt = currentSchedule.runDuringOption || "Financial Hour";
      schedule.startHour = opt === "Financial Hour" ? 8 : opt === "Non Financial Hour" ? 18 : 0;
      schedule.endHour = opt === "Financial Hour" ? 18 : opt === "Non Financial Hour" ? 23 : 23;
    }
    return schedule;
  };

  const handleAddSchedule = () => {
    if (!currentSchedule.date || !currentSchedule.time) {
      alert("Date and Time are required");
      return;
    }
    const schedule = buildScheduleFromCurrent();
    setSchedules((prev) => [...prev, schedule]);
    setCurrentSchedule({
      date: "",
      time: "",
      timezone: "Asia/Kolkata",
      type: "once",
      repeatEvery: 1,
      repeatUnit: "Hour",
      repeatDays: { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false },
      endOption: "after",
      endsOn: "",
      endsAfter: 10,
      runDuringOption: "Financial Hour",
    });
  };

  const handleScheduleRepeatEveryChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setCurrentSchedule((s) => ({ ...s, repeatEvery: "" }));
      return;
    }
    let val = parseInt(raw, 10);
    const max = SCHEDULE_MAX_REPEAT[currentSchedule.repeatUnit] || 1;
    if (isNaN(val)) val = 1;
    else if (val < 1) val = 1;
    else if (val > max) val = max;
    setCurrentSchedule((s) => ({ ...s, repeatEvery: val }));
  };

  const handleScheduleDayToggle = (dayName) => {
    setCurrentSchedule((s) => ({
      ...s,
      repeatDays: { ...(s.repeatDays || {}), [dayName]: !(s.repeatDays || {})[dayName] },
    }));
  };

  const headingStyle = {
    color: "#3730a3",
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "24px",
    lineHeight: "1.2"
  };

  const closeButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginBottom: "20px",
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none"
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer"
  };

  const textareaStyle = {
    ...inputStyle,
    resize: "vertical",
    minHeight: "100px",
    fontFamily: "inherit"
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#3730a3"
  };

  const buttonStyle = {
    padding: "12px 24px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "background-color 0.2s"
  };

  const deleteButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#dc2626",
    marginLeft: "12px",
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalContentStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "24px",
    maxWidth: "420px",
    width: "90%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  };

  const modalTitleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "8px",
  };

  const modalBodyStyle = {
    fontSize: "14px",
    color: "#4b5563",
    marginBottom: "16px",
  };

  const modalInputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ef4444",
    borderRadius: "6px",
    boxSizing: "border-box",
    marginBottom: "16px",
  };

  const modalButtonsStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  };

  const modalCancelButtonStyle = {
    padding: "8px 16px",
    fontSize: "14px",
    backgroundColor: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const modalDeleteButtonStyle = {
    padding: "8px 16px",
    fontSize: "14px",
    backgroundColor: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const modalDeleteButtonDisabledStyle = {
    ...modalDeleteButtonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
  };

  const formGroupStyle = {
    marginBottom: "20px"
  };

  return (
    <div>
      {onClose && (
        <button style={closeButtonStyle} onClick={onClose}>
          Close
        </button>
      )}
      <h2 style={headingStyle}>Edit Agent</h2>
      <form onSubmit={handleSubmit}>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Agent Library ID
          </label>
          <input
            type="text"
            value={agentLibraryId}
            onChange={(e) => setAgentLibraryId(e.target.value)}
            style={{
              ...inputStyle,
              width: "calc(100% - 100px)",
              marginRight: "10px",
            }}
          />
          <button
            type="button"
            onClick={handleFetch}
            style={buttonStyle}
          >
            Fetch
          </button>
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Custom Agent Name
          </label>
          <input
            type="text"
            value={customAgentName}
            onChange={(e) => setCustomAgentName(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Custom Agent Flow ID
          </label>
          <input
            type="text"
            value={customAgentFlowId}
            onChange={(e) => setCustomAgentFlowId(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Category
          </label>
          <select
            value={categoryIsCustom ? "__new__" : (category || "")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__new__") {
                setCategoryIsCustom(true);
                setCategory("");
              } else {
                setCategoryIsCustom(false);
                setCategory(v);
              }
            }}
            style={selectStyle}
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            <option value="__new__">Add new</option>
          </select>
          {categoryIsCustom && (
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Enter new category"
              style={{ ...inputStyle, marginTop: "8px" }}
            />
          )}
        </div>

        <IndustryContextPicker
          value={industryContext}
          onChange={setIndustryContext}
          inputStyle={inputStyle}
          selectStyle={selectStyle}
          labelStyle={labelStyle}
          formGroupStyle={formGroupStyle}
        />

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="4"
            style={textareaStyle}
          />
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Instructions
          </label>
          <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "10px" }}>
            Add one instruction per step. Drag the handle to reorder. They will be sent in order, joined by &quot; || &quot;.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {instructionSteps.map((step, index) => (
              <div
                key={step.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverStepIndex(index);
                }}
                onDragLeave={() => setDragOverStepIndex(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                  if (Number.isNaN(fromIndex)) return;
                  reorderInstructionSteps(fromIndex, index);
                  setDraggedStepIndex(null);
                  setDragOverStepIndex(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  padding: "10px 12px",
                  backgroundColor: dragOverStepIndex === index ? "#eef2ff" : "#fafafa",
                  opacity: draggedStepIndex === index ? 0.6 : 1,
                  transition: "background-color 0.15s ease",
                }}
              >
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", String(index));
                    e.dataTransfer.effectAllowed = "move";
                    setDraggedStepIndex(index);
                  }}
                  onDragEnd={() => setDraggedStepIndex(null)}
                  style={{
                    flexShrink: 0,
                    cursor: draggedStepIndex === index ? "grabbing" : "grab",
                    color: "#6b7280",
                    padding: "4px",
                    marginTop: "4px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Drag to reorder"
                >
                  <RiDragMove2Line size={18} />
                </div>
                <span
                  style={{
                    flexShrink: 0,
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "#3730a3",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "600",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "6px",
                  }}
                >
                  {index + 1}
                </span>
                <AutoResizeTextarea
                  value={step.content}
                  onChange={(e) => {
                    setInstructionSteps((prev) =>
                      prev.map((s) =>
                        s.id === step.id ? { ...s, content: e.target.value } : s
                      )
                    );
                  }}
                  placeholder="Enter step instruction..."
                  rows={2}
                  style={{
                    ...textareaStyle,
                    flex: 1,
                    minHeight: "56px",
                    margin: 0,
                    backgroundColor: "#fff",
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (instructionSteps.length > 1) {
                      setInstructionSteps((prev) => prev.filter((s) => s.id !== step.id));
                    }
                  }}
                  disabled={instructionSteps.length <= 1}
                  title={instructionSteps.length <= 1 ? "Keep at least one step" : "Remove step"}
                  style={{
                    flexShrink: 0,
                    padding: "6px 10px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    cursor: instructionSteps.length <= 1 ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                    opacity: instructionSteps.length <= 1 ? 0.6 : 1,
                    marginTop: "4px",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setInstructionSteps((prev) => [...prev, { id: stepId(), content: "" }])}
            style={{
              marginTop: "10px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: "600",
              color: "#3730a3",
              backgroundColor: "#eef2ff",
              border: "1px solid #3730a3",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            + Add Step
          </button>
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Organisation Visibility
          </label>
          <div style={{ maxWidth: 480 }}>
            <MultiSelectDropdown
              options={buildOrgMultiSelectOptions(availableOrgs, selectedOrgIds)}
              value={selectedOrgIds.map((id) => String(id))}
              onChange={(vals) => setSelectedOrgIds(parseOrgIdsFromMultiSelectValues(vals))}
              placeholder="Nothing Selected means Shown to All"
              searchable
              optionColumns={2}
              aria-label="Organisation visibility"
            />
          </div>
          <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#6b7280" }}>
            If nothing is selected, it will be shown to all.
          </p>
        </div>

        <div style={formGroupStyle}>
          <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ flex: "1" }}>
              <label style={labelStyle}>
                Lookback Period
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "14px", color: "#1f2937", fontWeight: "500" }}>Last</span>
                <input
                  type="number"
                  value={lookbackPeriod}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setLookbackPeriod("");
                      return;
                    }
                    if (/^\d+$/.test(value)) setLookbackPeriod(value);
                  }}
                  placeholder="1"
                  min={1}
                  step={1}
                  style={{
                    ...inputStyle,
                    width: "72px",
                    textAlign: "center",
                    padding: "10px 12px",
                    border: (() => {
                      const v = (lookbackPeriod || "").toString().trim();
                      const isInvalid = v === "" || v === "0";
                      return isInvalid ? "1px solid #ef4444" : "1px solid #3730a3";
                    })(),
                  }}
                />
                <div style={{ position: "relative", display: "inline-block" }}>
                  <select
                    value={lookbackUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      let newValue = lookbackPeriod;
                      if (!newValue || newValue === "0" || parseInt(newValue, 10) < 1) {
                        newValue = "1";
                      }
                      setLookbackUnit(newUnit);
                      if (newValue !== lookbackPeriod) setLookbackPeriod(newValue);
                    }}
                    style={{
                      ...selectStyle,
                      width: "160px",
                      paddingRight: "32px",
                      appearance: "none",
                    }}
                  >
                    <option value="Day">Day</option>
                    <option value="Month">Month</option>
                    <option value="Quarter">Quarter</option>
                    <option value="Year">Year</option>
                    <option value="Financial Year">Financial Year</option>
                  </select>
                  <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#6b7280", fontSize: "12px" }}>▼</span>
                </div>
              </div>
              {((lookbackPeriod || "").toString().trim() === "" || (lookbackPeriod || "").toString().trim() === "0") && (
                <div style={{ marginTop: "6px", fontSize: "12px", color: "#dc2626" }}>Lookback Period cannot be blank or 0</div>
              )}
            </div>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Writing Style
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <select
              value={writingStyle}
              onChange={(e) => setWritingStyle(e.target.value)}
              style={{ ...selectStyle, width: "220px" }}
            >
              <option value="">— Select —</option>
              <option value="detailed_report">Detailed Report</option>
              <option value="short_report">Short Report</option>
              <option value="tabular">Tabular</option>
              <option value="bullet_points">Bullet Points</option>
              <option value="simplify_real_scenarios">Simplify Real Scenarios</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {writingStyle === "custom" && (
            <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px", maxWidth: "500px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: "500", color: "#4b5563" }}>Custom style ID (optional)</label>
                <input
                  type="text"
                  value={customWritingStyleId}
                  onChange={(e) => setCustomWritingStyleId(e.target.value)}
                  placeholder="e.g. custom style id"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: "500", color: "#4b5563" }}>Custom style name</label>
                <input
                  type="text"
                  value={customWritingStyleName}
                  onChange={(e) => setCustomWritingStyleName(e.target.value)}
                  placeholder="Custom writing style name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: "500", color: "#4b5563" }}>Custom style description</label>
                <textarea
                  value={customWritingStyleDescription}
                  onChange={(e) => setCustomWritingStyleDescription(e.target.value)}
                  placeholder="Describe the custom writing style"
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", minHeight: "80px" }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Web Links
          </label>
          <div style={{ display: "flex", marginBottom: "10px" }}>
            <input
              type="text"
              value={currentLink}
              onChange={(e) => setCurrentLink(e.target.value)}
              style={{
                ...inputStyle,
                width: "calc(100% - 120px)",
                marginRight: "10px",
              }}
            />
            <button
              type="button"
              onClick={() => {
                if (currentLink) {
                  setWebLinks([...webLinks, currentLink]);
                  setCurrentLink("");
                }
              }}
              style={buttonStyle}
            >
              Add Link
            </button>
          </div>
          <div>
            {webLinks.map((link, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <span>{link}</span>
                <button
                  type="button"
                  onClick={() => {
                    setWebLinks(webLinks.filter((_, i) => i !== index));
                  }}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#ef4444",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Web Search
          </label>
          <div style={{ display: "inline-flex", borderRadius: "8px", border: "1px solid #d1d5db", overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setWebSearch("Off")}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(webSearch === "Off"
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              OFF
            </button>
            <button
              type="button"
              onClick={() => setWebSearch("On")}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(webSearch === "On"
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              ON
            </button>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Auto Data Selection
          </label>
          <div style={{ display: "inline-flex", borderRadius: "8px", border: "1px solid #d1d5db", overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setAutoDataSelection(false)}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(!autoDataSelection
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              OFF
            </button>
            <button
              type="button"
              onClick={() => setAutoDataSelection(true)}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(autoDataSelection
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              ON
            </button>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
            Select Form Types
          </label>
          
          <div style={{ display: "flex", gap: "20px" }}>
            {/* India Forms */}
            <div style={{ flex: 1 }}>
              <h4 style={{ color: "#3730a3", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>India</h4>
              <input
                type="text"
                placeholder="Search India forms..."
                value={formSearch.India}
                onChange={(e) => setFormSearch({ ...formSearch, India: e.target.value })}
                style={{ ...inputStyle, padding: "10px 12px", marginBottom: "10px", fontSize: "13px" }}
              />
              <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #3730a3", borderRadius: "6px", padding: "12px", backgroundColor: "#f9fafb" }}>
                {Object.entries(availableForms.India || {})
                  .filter(([key, description]) => 
                    key.toLowerCase().includes(formSearch.India.toLowerCase()) || 
                    description.toLowerCase().includes(formSearch.India.toLowerCase())
                  )
                  .map(([key, description]) => (
                  <div key={key} style={{ marginBottom: "5px" }}>
                    <label style={{ display: "flex", alignItems: "start", gap: "5px" }}>
                      <input
                        type="checkbox"
                        checked={(selectedForms.India || []).includes(key)}
                        onChange={(e) => handleFormSelection("India", key, e.target.checked)}
                      />
                      <span>
                        <strong>{key}</strong>: {description}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* USA Forms */}
            <div style={{ flex: 1 }}>
              <h4 style={{ color: "#3730a3", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>USA</h4>
              <input
                type="text"
                placeholder="Search USA forms..."
                value={formSearch.USA}
                onChange={(e) => setFormSearch({ ...formSearch, USA: e.target.value })}
                style={{ ...inputStyle, padding: "10px 12px", marginBottom: "10px", fontSize: "13px" }}
              />
              <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #3730a3", borderRadius: "6px", padding: "12px", backgroundColor: "#f9fafb" }}>
                {Object.entries(availableForms.USA || {})
                  .filter(([key, description]) => 
                    key.toLowerCase().includes(formSearch.USA.toLowerCase()) || 
                    description.toLowerCase().includes(formSearch.USA.toLowerCase())
                  )
                  .map(([key, description]) => (
                  <div key={key} style={{ marginBottom: "5px" }}>
                    <label style={{ display: "flex", alignItems: "start", gap: "5px" }}>
                      <input
                        type="checkbox"
                        checked={(selectedForms.USA || []).includes(key)}
                        onChange={(e) => handleFormSelection("USA", key, e.target.checked)}
                      />
                      <span>
                        <strong>{key}</strong>: {description}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Agent Scheduling
          </label>
          <div style={{ border: "1px solid #3730a3", padding: "20px", borderRadius: "6px", backgroundColor: "#f9fafb" }}>
            {/* Date and Time */}
            <div style={{ display: "flex", gap: "16px", marginBottom: "12px", flexWrap: "wrap" }}>
              <div style={{ flex: "1", minWidth: "120px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Date</label>
                <input
                  type="date"
                  value={currentSchedule.date}
                  onChange={(e) => setCurrentSchedule((s) => ({ ...s, date: e.target.value }))}
                  style={{ ...inputStyle, padding: "10px 12px" }}
                />
              </div>
              <div style={{ flex: "1", minWidth: "120px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Time</label>
                <input
                  type="time"
                  value={currentSchedule.time}
                  onChange={(e) => setCurrentSchedule((s) => ({ ...s, time: e.target.value }))}
                  style={{ ...inputStyle, padding: "10px 12px" }}
                />
              </div>
            </div>
            {/* Timezone */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Timezone</label>
              <select
                value={currentSchedule.timezone}
                onChange={(e) => setCurrentSchedule((s) => ({ ...s, timezone: e.target.value }))}
                style={{ ...selectStyle, padding: "10px 12px", maxWidth: "320px" }}
              >
                {SCHEDULE_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            {/* Repeat */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Repeat</label>
              <select
                value={currentSchedule.type}
                onChange={(e) => setCurrentSchedule((s) => ({ ...s, type: e.target.value }))}
                style={{ ...selectStyle, padding: "10px 12px", maxWidth: "320px" }}
              >
                <option value="once">Don&apos;t repeat</option>
                <option value="hourly">Every hour</option>
                <option value="daily">Daily</option>
                <option value="weekly">Every Week</option>
                <option value="monthly">Every Month</option>
                <option value="quarterly">Every Quarter</option>
                <option value="weekday">Every Weekday (Mon-Fri)</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {/* Custom: repeat every + unit */}
            {currentSchedule.type === "custom" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "500", color: "#374151", width: "90px" }}>Repeat every:</label>
                  <input
                    type="number"
                    min={1}
                    value={currentSchedule.repeatEvery === "" ? "" : currentSchedule.repeatEvery}
                    onChange={handleScheduleRepeatEveryChange}
                    style={{ ...inputStyle, width: "64px", padding: "8px", textAlign: "center" }}
                  />
                  <select
                    value={currentSchedule.repeatUnit}
                    onChange={(e) => setCurrentSchedule((s) => ({ ...s, repeatUnit: e.target.value }))}
                    style={{ ...selectStyle, width: "100px", padding: "8px" }}
                  >
                    <option value="Minute">Minute</option>
                    <option value="Hour">Hour</option>
                    <option value="Day">Day</option>
                    <option value="Week">Week</option>
                    <option value="Month">Month</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "500", color: "#374151", width: "90px" }}>Repeat on:</label>
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {SCHEDULE_DAY_MAP.map(({ label, name }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => handleScheduleDayToggle(name)}
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          border: "2px solid #3730a3",
                          background: (currentSchedule.repeatDays || {})[name] ? "#3730a3" : "transparent",
                          color: (currentSchedule.repeatDays || {})[name] ? "#fff" : "#3730a3",
                          fontWeight: "600",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", fontWeight: "500", color: "#374151" }}>Ends:</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="radio" name="endOption" checked={currentSchedule.endOption === "never"} onChange={() => setCurrentSchedule((s) => ({ ...s, endOption: "never" }))} />
                      <span style={{ fontSize: "14px" }}>Never</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="radio" name="endOption" checked={currentSchedule.endOption === "on"} onChange={() => setCurrentSchedule((s) => ({ ...s, endOption: "on" }))} />
                      <span style={{ fontSize: "14px" }}>On</span>
                      <input type="date" value={currentSchedule.endsOn || ""} onChange={(e) => setCurrentSchedule((s) => ({ ...s, endsOn: e.target.value }))} disabled={currentSchedule.endOption !== "on"} style={{ ...inputStyle, marginLeft: "8px", width: "140px", padding: "6px" }} />
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="radio" name="endOption" checked={currentSchedule.endOption === "after"} onChange={() => setCurrentSchedule((s) => ({ ...s, endOption: "after" }))} />
                      <span style={{ fontSize: "14px" }}>After</span>
                      <input type="number" min={1} value={currentSchedule.endsAfter ?? 10} onChange={(e) => setCurrentSchedule((s) => ({ ...s, endsAfter: parseInt(e.target.value, 10) || 10 }))} disabled={currentSchedule.endOption !== "after"} style={{ ...inputStyle, width: "60px", marginLeft: "8px", padding: "6px", textAlign: "center" }} />
                      <span style={{ fontSize: "14px", marginLeft: "4px" }}>runs</span>
                    </label>
                  </div>
                </div>
              </>
            )}
            {/* Run during (hourly or custom minute/hour) */}
            {(currentSchedule.type === "hourly" || (currentSchedule.type === "custom" && ["Minute", "Hour"].includes(currentSchedule.repeatUnit))) && (
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "500", color: "#374151" }}>Run during:</label>
                <select
                  value={currentSchedule.runDuringOption || "Financial Hour"}
                  onChange={(e) => setCurrentSchedule((s) => ({ ...s, runDuringOption: e.target.value }))}
                  style={{ ...selectStyle, padding: "8px", maxWidth: "280px" }}
                >
                  <option value="Financial Hour">Financial Hour (8am - 6pm)</option>
                  <option value="Non Financial Hour">Non Financial Hour</option>
                  <option value="Both">Both</option>
                </select>
              </div>
            )}
            <button type="button" onClick={handleAddSchedule} style={{ ...buttonStyle, marginBottom: "10px" }}>
              Add Schedule
            </button>
            {schedules.length > 0 && (
              <div style={{ marginTop: "12px", borderTop: "1px solid #e5e7eb", paddingTop: "12px" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#3730a3" }}>Schedules added (stored in metadata):</div>
                {schedules.map((sch, index) => (
                  <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", backgroundColor: "#fff", padding: "10px 12px", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: "14px" }}>
                      {sch.date} {sch.time} ({sch.timezone}) — {sch.type}
                      {sch.repeatEvery != null && sch.repeatUnit && ` · every ${sch.repeatEvery} ${sch.repeatUnit}`}
                      {sch.repeatOn && sch.repeatOn.length > 0 && ` · on ${sch.repeatOn.join(", ")}`}
                      {sch.endsOn && ` · ends ${sch.endsOn}`}
                      {sch.endsAfter != null && sch.endsAfter > 0 && ` · after ${sch.endsAfter} runs`}
                    </span>
                    <button type="button" onClick={() => setSchedules((prev) => prev.filter((_, i) => i !== index))} style={{ padding: "6px 12px", backgroundColor: "#ef4444", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "8px" }}>
          <button
            type="submit"
            style={buttonStyle}
          >
            Save Changes
          </button>
          <button
            type="button"
            style={deleteButtonStyle}
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        </div>
      </form>

      {showDeleteModal && (
        <div style={modalOverlayStyle} onClick={handleDeleteCancel}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>Delete library agent</div>
            <p style={modalBodyStyle}>
              Are you sure you want to delete this library agent from the database? This action cannot be undone.
            </p>
            <p style={{ ...modalBodyStyle, marginBottom: "8px" }}>
              Type <strong>delete</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="delete"
              style={modalInputStyle}
              autoFocus
              aria-label="Type delete to confirm"
            />
            <div style={modalButtonsStyle}>
              <button type="button" style={modalCancelButtonStyle} onClick={handleDeleteCancel} disabled={deleting}>
                Cancel
              </button>
              <button
                type="button"
                style={(deleteConfirmText || "").trim().toLowerCase() === "delete" ? modalDeleteButtonStyle : modalDeleteButtonDisabledStyle}
                onClick={handleDeleteConfirm}
                disabled={deleting || (deleteConfirmText || "").trim().toLowerCase() !== "delete"}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAgent;
